import { UseFormReset, UseFormSetError } from "react-hook-form";
import Form from "../../forms/Form";
import Checkbox from "../../forms/inputs/Checkbox";
import SettingsTextBoxV2 from "../inputs/SettingsTextBoxV2";
import useUserDetails from "../../../utils/hooks/user/useUserDetails";
import { changePassword, setupTOTPApi, enableTOTPApi, disableTOTPApi, requestDisableOTP } from "../../../utils/api/endpoints/user/user";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../../buttons/PrimaryButton";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";

type OTPFieldValues = {
  otpEnabled?: boolean;
};

type PasswordFieldValues = {
  current: string;
  newPassword: string;
  confirmPassword: string;
};

type TOTPSetupState = {
  qrCode: string;
  backupCodes: string[];
  setupToken: string;
};

/**
 * Settings page to let users
 * update their security details:
 *
 * - Password
 * - Toggle 2FA & Configure TOTP
 */
const Security = () => {
  const navigate = useNavigate();
  const { otpEnabled, setOtpEnabled, fetchDetails, totpSecret } = useUserDetails();
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState<TOTPSetupState | null>(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState("");
  const [isSetupSuccess, setIsSetupSuccess] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const updatePassword = async (
    data: PasswordFieldValues,
    setError: UseFormSetError<PasswordFieldValues>,
    reset: UseFormReset<PasswordFieldValues>
  ) => {
    if (data.confirmPassword !== data.newPassword) {
      setError("confirmPassword", {
        message: "Passwords don't match"
      });
      return;
    }

    try {
      await changePassword(data.current, data.newPassword);
      toast.success("Password changed successfully");
      reset();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Password change failed");
    }
  };

  const handleSetupTOTP = async () => {
    try {
      const data = await setupTOTPApi();
      setSetupData(data);
      setIsSetupSuccess(false);
      setShowSetup(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to start TOTP setup");
    }
  };

  const handleVerifySetup = async () => {
    if (!totpVerifyCode || !setupData) return;
    try {
      await enableTOTPApi(totpVerifyCode, setupData.setupToken);
      toast.success("TOTP MFA enabled successfully");
      fetchDetails();
      setIsSetupSuccess(true);
      setTotpVerifyCode("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Verification failed");
    }
  };

  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [disable2FACode, setDisable2FACode] = useState("");

  const handleDisableTOTP = async () => {
    if (!disableCode) return;
    try {
      await disableTOTPApi(disableCode);
      toast.success("TOTP MFA disabled successfully");
      fetchDetails();
      setShowDisableDialog(false);
      setDisableCode("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to disable TOTP");
    }
  };

  const update2FA = async (data: OTPFieldValues) => {
    if (otpEnabled && !data.otpEnabled) {
      // Prompt verification modal if user is disabling 2FA
      setShowDisable2FAModal(true);
      if (!totpSecret) {
        try {
          await requestDisableOTP();
          toast.success("Verification code sent to your registered email");
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to send verification code");
        }
      }
    } else {
      setOtpEnabled(!!data.otpEnabled);
    }
  };

  const handleConfirmDisable2FA = async () => {
    if (!disable2FACode) {
      toast.error("Please enter the verification code to proceed");
      return;
    }
    try {
      await setOtpEnabled(false, disable2FACode);
      setShowDisable2FAModal(false);
      setDisable2FACode("");
    } catch (error) {
      // Toast message generated inside hook
    }
  };

  const defaultValues = useMemo(() => ({ otpEnabled }), [otpEnabled]);
  const passwordDefaultValues = useMemo<PasswordFieldValues>(
    () => ({ current: "", newPassword: "", confirmPassword: "" }),
    []
  );

  return (
    <div className="text-sm">
      <h2 className="text-2xl text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Security Settings</h2>
      
      {/* Password card */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 mb-6 shadow">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200 -mx-5 md:-mx-6 px-5 md:px-6">
          <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
          <h3 className="text-base font-medium text-gray-900">Password</h3>
        </div>

        <Form<PasswordFieldValues>
          defaultValues={passwordDefaultValues}
          onSubmit={updatePassword}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <SettingsTextBoxV2 name="current" password withVisibilityToggle rules={{ required: true }} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <SettingsTextBoxV2
              name="newPassword"
              password
              withVisibilityToggle
              rules={{
                required: true,
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                  message: "Weak password"
                }
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <SettingsTextBoxV2 name="confirmPassword" password rules={{ required: true }} />
          </div>

          <div className="flex justify-end">
            <PrimaryButton type="submit" className="mt-1 !bg-[#010D2C] hover:!bg-[#010D2C]/90">Update Password</PrimaryButton>
          </div>
        </Form>
      </section>

      {/* 2FA card */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 mb-6 shadow">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200 -mx-5 md:-mx-6 px-5 md:px-6">
          <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          <h3 className="text-base text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Two-Factor Authentication (Fallback Method)</h3>
        </div>

        <Form<OTPFieldValues> defaultValues={defaultValues} onSubmit={update2FA}>
          <div className="flex items-start gap-3">
            <Checkbox name="otpEnabled" label="Enable default email 2FA authentication" className="text-sm text-[#374151]" checkboxClassName="text-[#010D2C] focus:ring-[#010D2C]" />
          </div>
          <p className="text-sm text-gray-500 mt-2">Requires an alphanumeric OTP sent to your registered email when logging in. Ideal as a fallback method.</p>

          {!otpEnabled && (
            <div className="mt-4 rounded-md bg-[#58CC02]/[0.17] p-4 text-[#58CC02] text-sm border-l-4 border-[#58CC02]">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <p>
                  Your account is less secure without two-factor authentication. We strongly recommend enabling this feature.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <PrimaryButton type="submit" className="!bg-[#010D2C] hover:!bg-[#010D2C]/90">Update Fallback 2FA</PrimaryButton>
          </div>
        </Form>
      </section>

      {/* TOTP Authenticator App card */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow mb-6">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200 -mx-5 md:-mx-6 px-5 md:px-6">
          <svg className="w-5 h-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>
          <h3 className="text-base text-gray-900 font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>TOTP Authenticator Application</h3>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Use standard authentication apps like Google Authenticator, Authy, or Microsoft Authenticator to generate secure, rotating 6-digit verification codes offline.
          </p>

          {!otpEnabled && !totpSecret ? (
            <div className="rounded-md bg-yellow-50 p-4 border-l-4 border-yellow-400 text-yellow-800 text-sm">
              Please enable the default email 2FA first before configuring an Authenticator app.
            </div>
          ) : (
            <div className="flex justify-start gap-4">
              {!totpSecret ? (
                <PrimaryButton onClick={handleSetupTOTP} className="!bg-[#010D2C] hover:!bg-[#010D2C]/90">
                  Setup Authenticator App
                </PrimaryButton>
              ) : (
                <button onClick={() => setShowDisableDialog(true)} className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition">
                  Disable Authenticator App
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Setup Modal */}
      {showSetup && setupData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            <button onClick={() => { setShowSetup(false); setSetupData(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Set up Authenticator</h3>
            
            <div className="space-y-4">
              <div className="flex flex-col items-center">
                <img src={setupData.qrCode} alt="TOTP QR Code" className="w-48 h-48 border border-gray-100 rounded-lg mb-2" />
                <p className="text-xs text-gray-500 text-center">Scan this QR Code in Google Authenticator or Microsoft Authenticator.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-semibold text-gray-700">Save backup recovery codes (store securely):</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(setupData.backupCodes.join("\n"));
                        toast.success("Recovery codes copied to clipboard!");
                      }}
                      className="text-xs text-[#010D2C] hover:underline font-semibold"
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const element = document.createElement("a");
                        const file = new Blob([setupData.backupCodes.join("\n")], { type: 'text/plain' });
                        element.href = URL.createObjectURL(file);
                        element.download = "ctb-mfa-backup-codes.txt";
                        document.body.appendChild(element);
                        element.click();
                        document.body.removeChild(element);
                        toast.success("Codes downloaded!");
                      }}
                      className="text-xs text-[#010D2C] hover:underline font-semibold"
                    >
                      Download
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm text-[#010D2C]">
                  {setupData.backupCodes.map((code, idx) => (
                    <div key={idx} className="bg-white px-2 py-1 rounded border border-gray-200 text-center">{code}</div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Verify Authenticator Code</label>
                <input
                  type="text"
                  placeholder={isSetupSuccess ? "Verified" : "Enter 6-digit code"}
                  value={isSetupSuccess ? "" : totpVerifyCode}
                  disabled={isSetupSuccess}
                  onChange={(e) => setTotpVerifyCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#010D2C] text-center tracking-widest text-lg font-semibold disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <PrimaryButton 
                onClick={handleVerifySetup} 
                disabled={isSetupSuccess} 
                className={`w-full justify-center ${isSetupSuccess ? "!bg-green-600 hover:!bg-green-600 text-white cursor-default" : "!bg-[#010D2C] hover:!bg-[#010D2C]/90"}`}
              >
                {isSetupSuccess ? "Configured ✓" : "Verify and Complete Setup"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Disable Modal */}
      {showDisableDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            <button onClick={() => setShowDisableDialog(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Disable Authenticator App</h3>
            <p className="text-sm text-gray-500 mb-4">Please confirm by entering your current Authenticator app code or one of your backup recovery codes.</p>

            <div className="space-y-4">
              <input
                type="text"
                placeholder="6-digit code or backup code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-center tracking-widest text-lg font-semibold"
              />

              <div className="flex gap-3">
                <button onClick={() => setShowDisableDialog(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button onClick={handleDisableTOTP} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition">
                  Confirm Disable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showDisable2FAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            <button onClick={() => { setShowDisable2FAModal(false); setDisable2FACode(""); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Disable Two-Factor Authentication</h3>
            <p className="text-sm text-gray-500 mb-4">
              {totpSecret 
                ? "Please verify your current Authenticator app code or one of your backup recovery codes to confirm disabling two-factor authentication."
                : "Please confirm you wish to disable email fallback two-factor authentication."}
            </p>

            <div className="space-y-4">
              <input
                type="text"
                placeholder={totpSecret ? "6-digit code or backup code" : "4-digit Email OTP"}
                value={disable2FACode}
                onChange={(e) => setDisable2FACode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-center tracking-widest text-lg font-semibold"
              />

              <div className="flex gap-3">
                <button onClick={() => { setShowDisable2FAModal(false); setDisable2FACode(""); }} className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button onClick={handleConfirmDisable2FA} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition">
                  Confirm Disable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Security;
